import axios from 'axios';

function normalizeBaseUrl(rawUrl) {
  if (!rawUrl) return '';
  const trimmed = String(rawUrl).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('/')) return '';
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed.replace(/\/$/, '');
  }
  // If protocol missing, default to https for production hosts
  return `https://${trimmed.replace(/\/$/, '')}`;
}

const RAW_API_BASE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL)
  || (typeof window !== 'undefined' && window.__AHEAD_API_URL__)
  || (typeof window !== 'undefined' && window.location && window.location.hostname.includes('vercel.app') 
      ? 'https://ahead-production-5465.up.railway.app' 
      : '')
  || '';

export const API_BASE_URL = normalizeBaseUrl(RAW_API_BASE);

// Fallback for production if env var is not working
const FALLBACK_API_URL = 'https://ahead-production-5465.up.railway.app';
const FINAL_API_BASE_URL = API_BASE_URL || FALLBACK_API_URL;

// Debug logging for production issues
if (typeof window !== 'undefined') {
  console.log('API Configuration Debug:');
  console.log('- RAW_API_BASE:', RAW_API_BASE);
  console.log('- API_BASE_URL:', API_BASE_URL);
  console.log('- FINAL_API_BASE_URL:', FINAL_API_BASE_URL);
  console.log('- process.env.REACT_APP_API_URL:', typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL);
  console.log('- window.location.hostname:', typeof window !== 'undefined' && window.location && window.location.hostname);
  console.log('- All process.env keys:', typeof process !== 'undefined' && process.env ? Object.keys(process.env).filter(k => k.startsWith('REACT_APP_')) : 'N/A');
}

const api = axios.create({
  baseURL: FINAL_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Simple in-memory cache for GET requests
const cache = new Map();
const CACHE_TTL = 30 * 1000; // 30 seconds

// Track in-flight requests for deduplication
const inFlightRequests = new Map();

// Generate cache key from request config
function getCacheKey(config) {
  return `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
}

// Generate request key for deduplication (same as cache key)
function getRequestKey(config) {
  return getCacheKey(config);
}

// Check if cache entry is still valid
function isCacheValid(entry) {
  return Date.now() - entry.timestamp < CACHE_TTL;
}

// Wrapper to handle request deduplication and caching
const originalRequest = api.request.bind(api);

// Override request method to add deduplication
api.request = function(config) {
  // Only deduplicate GET requests
  if ((config.method === 'get' || config.method === 'GET' || !config.method)) {
    const requestKey = getRequestKey(config);
    
    // Check for duplicate in-flight requests
    if (inFlightRequests.has(requestKey)) {
      return inFlightRequests.get(requestKey);
    }
    
    // Check cache first
    const cacheKey = getCacheKey(config);
    const cached = cache.get(cacheKey);
    if (cached && isCacheValid(cached)) {
      return Promise.resolve({
        data: cached.data,
        status: 200,
        statusText: 'OK',
        headers: cached.headers,
        config: config,
      });
    }
    
    // Make the request and track it
    const requestPromise = originalRequest(config)
      .then(response => {
        inFlightRequests.delete(requestKey);
        return response;
      })
      .catch(error => {
        inFlightRequests.delete(requestKey);
        throw error;
      });
    
    inFlightRequests.set(requestKey, requestPromise);
    return requestPromise;
  }
  
  // For non-GET requests, just make the request
  return originalRequest(config);
};

// Add request interceptor for caching GET requests (for response caching)
api.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle caching and 401 errors
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' || response.config.method === 'GET') {
      const cacheKey = getCacheKey(response.config);
      cache.set(cacheKey, {
        data: response.data,
        headers: response.headers,
        timestamp: Date.now(),
      });
    }
    
    // Invalidate cache on mutations (POST, PUT, DELETE, PATCH)
    if (['post', 'put', 'delete', 'patch'].includes(response.config.method?.toLowerCase())) {
      // Clear all task-related caches on mutations
      const keysToDelete = [];
      cache.forEach((value, key) => {
        if (key.includes('/api/tasks') || key.includes('/api/categories')) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => cache.delete(key));
    }
    
    return response;
  },
  (error) => {
    // Handle cached responses
    if (error.__cached) {
      return Promise.resolve({
        data: error.data,
        status: error.status,
        statusText: error.statusText,
        headers: error.headers,
        config: error.config,
      });
    }
    
    // Handle deduplicated requests - return the existing promise
    if (error.__deduplicated) {
      return error.promise;
    }
    
    // Handle in-flight requests - return the tracked promise
    if (error.__inFlight) {
      return error.promise;
    }
    
    // Handle 401 errors
    if (error.response?.status === 401) {
      // Token is invalid or expired
      console.log('401 Unauthorized - clearing token and redirecting to login');
      localStorage.removeItem('token');
      
      // Clear cache and in-flight requests on auth failure
      cache.clear();
      inFlightRequests.clear();
      
      // Only redirect if we're not already on login/register pages
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/register') && !currentPath.includes('/forgot-password') && !currentPath.includes('/reset-password')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Export function to manually clear cache if needed
export const clearApiCache = () => {
  cache.clear();
};

export default api;


